import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Deck, DeckFileFormat } from "@apiserver-rest/ponder-data-client-base";
import { DeckFormatInfos } from "@ponder-shared/utils/deckFormatInfo";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { canExportSideboard, exportDeck } from "./export/deck-exporter";
import { NgClass } from "@angular/common";
import { keepInBoundsX } from "@ponder-shared/utils/keepInBounds";

@Component({
    selector: "deck-export",
    imports: [NgClass, ReactiveFormsModule],
    templateUrl: "./deck-export-modal.component.html"
})
export class DeckExportComponent {
    @ViewChild("dialog", { static: false }) public dialogRef?: ElementRef<HTMLDivElement>;
    @Input({ required: true }) public deck!: Deck;
    
    public exportDeckForm!: FormGroup;

    public readonly exportSvgIcon: string = SvgIconMap.get(SvgIcon.Export)!;
    public readonly copySvgIcon: string = SvgIconMap.get(SvgIcon.Copy)!;
    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    public readonly allFormats: DeckFileFormat[] = Object.keys(DeckFileFormat) as DeckFileFormat[];

    public isVisible: boolean = false;
    public wasCopied: boolean = false;
    public canExportSideboard: boolean = true;

    public constructor(private elementRef: ElementRef, formBuilder: FormBuilder, private changeDetector: ChangeDetectorRef) {
        this.exportDeckForm = formBuilder.group({
            format: [ DeckFileFormat.Moxfield ],
            content: [ "" ],
            sideboard: [ true ]
        });
    }

    public ngOnInit(): void {
        this.exportDeckForm.get("format")?.valueChanges.subscribe((format: DeckFileFormat) => {
            this.updateFormat(format);
        });

        this.exportDeckForm.get("sideboard")?.valueChanges.subscribe((checked: boolean) => {
            this.updateContents();
        });

        this.canExportSideboard = canExportSideboard(this.exportDeckForm.get("format")!.value);
        this.updateContents();
    }
    
    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.isVisible && !this.elementRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        this.wasCopied = false;
        
        // Did the user click outside of our modal panel?
        if (this.isVisible && this.mouseDownOutside && !this.elementRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }
    
    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    public onExportDeckClicked(): void {
        if (this.isVisible) {
            this.onClose();
        } else {
            this.onOpen();
        }
    }

    public onOpen(): void {
        this.isVisible = true;
        
        if (!this.dialogRef) {
            this.changeDetector.detectChanges();
        }
        
        if (this.dialogRef) {
            keepInBoundsX(this.dialogRef.nativeElement, 10);
        }
    }

    public onClose(): void {
        this.isVisible = false;
        this.mouseDownOutside = false;
    }
        
    public getFileFormatDisplayName(format: DeckFileFormat): string {
        return DeckFormatInfos.get(format)?.displayName ?? "";
    }

    private updateFormat(format: DeckFileFormat): void {
        this.canExportSideboard = canExportSideboard(format);
        var sideboardCheckbox = this.exportDeckForm.get("sideboard");

        if (sideboardCheckbox) {
            if (this.canExportSideboard) {
                sideboardCheckbox.enable();
            } else {
                sideboardCheckbox.disable();
            }
        }

        this.updateContents();
    }

    private updateContents(): void {
        this.exportDeckForm.patchValue({
            content: exportDeck(this.deck, this.exportDeckForm.get("format")!.value, this.exportDeckForm.get("sideboard")!.value)
        });
    }

    public onCopyClicked(): void {
        navigator.clipboard.writeText(this.exportDeckForm.get("content")!.value);
        this.wasCopied = true;
    }
}